import { AimOutlined } from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import { type Feature } from 'ol';
import type Map from 'ol/Map';
import type VectorSource from 'ol/source/Vector';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { createHighlightStyle } from '../mapStyleUtils.ts';

/** Stile arancione usato per evidenziare la feature durante il lampeggio. */
const highlightStyle = createHighlightStyle(
  'rgba(255, 165, 0, 0.4)',
  '#ff8c00',
  3,
);

/** Intervallo tra uno step e l'altro dell'animazione di lampeggio (ms). */
const FLASH_INTERVAL_MS = 300;

/** Numero totale di step: 4 step = 2 lampeggi (acceso-spento-acceso-spento). */
const FLASH_STEPS = 4;

export interface IMapFlashFeatureButtonProps {
  /** Ref alla mappa OpenLayers. */
  mapRef: React.RefObject<Map | null>;
  /** Ref alla VectorSource che contiene le feature disegnate dall'utente. */
  featuresSource: React.RefObject<VectorSource>;
  /**
   * Indice della geometria da evidenziare.
   * Corrisponde a `geometryIndex` per le geometrie disegnate
   * e a `sourceGeometryIndex` per le righe di intersezione.
   * Accetta `null` perché gli schema Yup con `notRequired()` producono `number | undefined | null`.
   */
  geometryIndex?: number | null;
}

/**
 * Bottone autocontenuto "Vai alla geometria".
 *
 * Usato sia nella tabella geometrie disegnate (`MapGeometriesTable`)
 * sia nella tabella intersezioni (`MapIntersectionsTable`).
 *
 * Al click esegue tre azioni in sequenza:
 * 1. Scrolla la pagina fino a rendere visibile la mappa
 * 2. Zoom della mappa sull'extent della feature
 * 3. Flash: 2 lampeggi arancioni (4 step a 300ms ciascuno)
 *
 * Il lookup della feature avviene cercando nella `featuresSource`
 * la feature con proprietà `geometryIndex` uguale al prop ricevuto.
 *
 * Non renderizza nulla se `geometryIndex` è null/undefined.
 */
export function MapFlashFeatureButton(props: IMapFlashFeatureButtonProps) {
  const { mapRef, featuresSource, geometryIndex } = props;
  const { t } = useTranslation();

  /**
   * Ref che tiene traccia del flash attivo (interval + feature).
   * Usato un ref (non state) perché l'animazione è un side-effect
   * gestito via setInterval, non serve triggerare re-render.
   */
  const activeFlash = useRef<{
    interval: ReturnType<typeof setInterval>;
    feature: Feature;
  } | null>(null);

  /** Ferma il flash in corso: cancella l'interval e resetta lo stile della feature. */
  const cancelActiveFlash = useCallback(() => {
    if (!activeFlash.current) return;
    clearInterval(activeFlash.current.interval);
    activeFlash.current.feature.setStyle();
    activeFlash.current = null;
  }, []);

  /**
   * Cleanup all'unmount: se il componente viene smontato mentre un flash
   * è in corso (es. la riga viene cancellata dalla tabella), il setInterval
   * verrebbe lasciato attivo in memoria. Il return dell'useEffect registra
   * una funzione di cleanup che React chiama automaticamente all'unmount.
   */
  useEffect(() => {
    return () => cancelActiveFlash();
  }, [cancelActiveFlash]);

  const handleClick = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;

    // Cerca la feature disegnata il cui geometryIndex corrisponde
    const feature = featuresSource.current
      ?.getFeatures()
      .find(feature => feature.get('geometryIndex') === geometryIndex);
    if (!feature) return;

    const geometry = feature.getGeometry();
    if (!geometry) return;

    // Se c'è un flash precedente ancora in corso, lo cancella
    cancelActiveFlash();

    // Scrolla la pagina fino alla mappa (che può essere fuori viewport)
    map
      .getTargetElement()
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // Zoom sull'extent della feature con animazione
    map.getView().fit(geometry.getExtent(), {
      padding: [64, 64, 64, 64],
      duration: 400,
      maxZoom: 18,
    });

    // Lampeggio: alterna stile arancione / default ogni 300ms per 4 step (2 blink)
    let step = 0;
    const interval = setInterval(() => {
      feature.setStyle(step % 2 === 0 ? highlightStyle : undefined);
      if (++step >= FLASH_STEPS) {
        clearInterval(interval);
        feature.setStyle();
        activeFlash.current = null;
      }
    }, FLASH_INTERVAL_MS);

    activeFlash.current = { interval, feature };
  }, [mapRef, featuresSource, geometryIndex, cancelActiveFlash]);

  if (geometryIndex == null) return null;

  return (
    <Tooltip
      title={t('map-widget.geometries.go-to-geometry', 'Vai alla geometria')}
    >
      <Button
        type="text"
        icon={<AimOutlined aria-hidden />}
        aria-label={t(
          'map-widget.geometries.go-to-geometry',
          'Vai alla geometria',
        )}
        onClick={handleClick}
      />
    </Tooltip>
  );
}
